import { ref, nextTick } from 'vue';
import { TranscriptAdoptionError, useSubtitleStore } from '@stores/subtitleStore';
import { notify } from '../NotificationPlugin';
import { useLogStore } from '@stores/logStore';
import { useSessionStore } from '@stores/session';
import AutoService from '@services/AutoService';
import type { Transcript, TranscriptSource, IOElement, IOItem } from '@lambda-libs/types';
import { ApiSrtLoader, FileSrtLoader, TextSrtLoader, JsonSrtLoader, isUntimedTextError } from '@components/SrtEditor/srtLoaders';
import type { SrtLoader } from '@components/SrtEditor/srtLoaders';
import { parseTranscriptDocument, timedDocumentToJson, TranscriptDocumentError } from '@lambda-libs/contracts/transcript-document';
import { useI18n } from 'vue-i18n';
import ConfirmationDialog from '@components/ConfirmationDialog.vue';
import { useDashboardStore } from '@stores/dashboardStore'; // Import the store
import { storeToRefs } from 'pinia'; // Import storeToRefs for reactive references
import ISO6391 from 'iso-639-1';
import { flushPendingSubtitleUpdates } from './UpdateWords';
import { useAuthUserStore } from '@stores/auth';
import { registerChangeListener } from '@stores/subtitleStore';
import { isDemoSourceId, buildDemoSource } from '@utils/demoTranscript';


// Create a singleton instance holder
let editorInstance:any = null;
let isInitialized = false;

// Autosave back-off: after a createInitialDraft failure the retry interval is
// doubled (capped at 8 min) and reset to the 30s base on success.
const AUTOSAVE_BASE_INTERVAL = 30000;
const AUTOSAVE_MAX_INTERVAL = 8 * 60 * 1000;
// Warn once about draft-creation failures; later failures go to the log only
// (prevents the same warning toast every 30s while creation keeps failing).
let draftWarningShown = false;

// Function to initialize the editor (to be called after Pinia is ready)
export function initializeTranscriptEditor() {
  if (isInitialized) {
    return editorInstance;
  }

  editorInstance = createTranscriptEditor();
  isInitialized = true;
  return editorInstance;
}

// Get the editor instance, initializing if needed. All call sites run in
// component setup after Pinia is installed, so lazy init here is safe —
// initialize silently instead of warning on every page load.
export function useTranscriptEditor() {
  if (!isInitialized) {
    return initializeTranscriptEditor();
  }
  return editorInstance;
}

function createTranscriptEditor() {
  const { t, locale } = useI18n();
  // Source-adoption UX strings live here, not in the global locale files
  // (owned by another workstream): English + Romanian, picked from the
  // active locale the same way the in-component messages do.
  const isRoLocale = (): boolean => {
    try {
      const current = String((locale as unknown as { value?: unknown })?.value ?? '');
      if (/^ro/i.test(current)) return true;
      if (/^en/i.test(current)) return false;
    } catch { /* fall through to the app default below */ }
    return true;
  };
  const adoptionText = (key: 'conflictTitle' | 'conflictBody' | 'conflictKept' | 'unavailable' | 'retryFailed' | 'availabilityUnknown'): string => {
    const ro = isRoLocale();
    switch (key) {
      case 'conflictTitle': return ro ? 'Reîncarci transcrierea?' : 'Reload transcript?';
      case 'conflictBody': return ro
        ? 'Au apărut modificări în timp ce se încărca noua transcriere. Au fost păstrate în documentul curent și vor fi salvate înainte de reîncărcare. Reîncarci acum?'
        : 'Edits arrived while the new transcript was loading. They were kept in the current document and will be saved before reloading. Reload now?';
      case 'conflictKept': return ro
        ? 'Au apărut modificări în timpul încărcării. Documentul curent și modificările au fost păstrate.'
        : 'Edits arrived while loading. The current document and its edits were kept.';
      case 'unavailable': return ro
        ? 'Istoricul transcrierii este temporar indisponibil. Documentul curent și modificările au fost păstrate. Reîncearcă.'
        : 'Transcript history is temporarily unavailable. Your current document and edits were kept. Please retry.';
      case 'retryFailed': return ro
        ? 'Reîncărcarea nu a reușit. Documentul curent și modificările au fost păstrate.'
        : 'Reload did not complete. Your current document and edits were kept.';
      case 'availabilityUnknown': return ro
        ? 'Disponibilitatea transcrierii este necunoscută. Reîncearcă.'
        : 'Transcript availability is unknown. Please retry.';
    }
  };
  const isAdoptionError = (error: unknown): error is TranscriptAdoptionError =>
    error instanceof TranscriptAdoptionError || (error as { name?: unknown })?.name === 'TranscriptAdoptionError';
  const currentTranscriptSource = ref<TranscriptSource | null>(null);
  const showSrtEditor = ref(false);
  // Pas 1.2 — demo fără cont: sursa demo trăiește doar local. Nicio salvare
  // în cont (drafturi/autosave) nu se încearcă vreodată pentru ea, altfel
  // vizitatorul neautentificat ar fi blocat la închiderea editorului
  // (closeSrtEditor cere saveCurrentChanges() === true).
  const isDemoMode = ref(false);
  // Untimed text transcripts use the dedicated text viewer/editor (no fake cues).
  const showTextEditor = ref(false);
  const isTextDocument = ref(false);
  const textTranscriptId = ref<string | null>(null);
  const textSourceId = ref<string | null>(null);
  const textFileName = ref('');
  const textLanguage = ref('');
  const textProvenance = ref('');
  // Tri-state like the backend: true = verified, false = confirmed missing,
  // undefined = unknown (no probe). Never coerce unknown to false — the text
  // view keeps the original download enabled and resolves it honestly.
  const textOriginalAvailable = ref<boolean | undefined>(false);
  const textInitialText = ref<string | null>(null);
  const textInitialDraftId = ref<string | null>(null);
  const textInitialDrafts = ref<any[]>([]);
  interface TextSaver {
    save: () => Promise<boolean>;
    isDirty: () => boolean;
    canClose?: () => Promise<boolean>;
    ready?: () => boolean;
    busy?: () => boolean;
  }
  let textSaver: TextSaver | null = null;
  const registerTextSaver = (saver: TextSaver) => { textSaver = saver; };
  const clearTextSaver = (saver?: TextSaver) => {
    if (saver && textSaver !== saver) return;
    textSaver = null;
  };
  const isTextSaverReady = () => !!textSaver && (!textSaver.ready || textSaver.ready()) && (!textSaver.busy || !textSaver.busy());
  // Observed by the shared SrtEditor mount for beforeunload/route guards.
  // A missing controller while the text view is shown is treated as unsaved:
  // the transition guard blocks in that state, so unload must warn too.
  const isTextDirty = (): boolean => {
    if (!showTextEditor.value) return false;
    if (!textSaver) return true;
    try { return textSaver.isDirty(); } catch { return true; }
  };
  const isTextBusy = (): boolean => {
    if (!showTextEditor.value) return false;
    if (!textSaver) return true;
    try {
      if (textSaver.busy?.() === true) return true;
      if (textSaver.ready && !textSaver.ready()) return true;
    } catch { return true; }
    return false;
  };
  const confirmationDialog = ref<typeof ConfirmationDialog>();
  const baseElement = ref<IOElement | null>(null);
  const deadlineTime = ref('');
  const showDraftModal = ref(false);
  const subtitleStore = useSubtitleStore();
  const currentDraftId = ref<string | null>(null);
  const availableDrafts = ref<any[]>([]);
  const editRevision = ref(0);
  let saveChain: Promise<boolean> = Promise.resolve(true);
  let creatingDraft: Promise<boolean> | undefined;
  let checkpointBusy = false;

  const buildFileUrl = (userId: string, projectId: string, parentId: string, elementId: string, secretId: string): string => {
    const domain = (useSessionStore().appConfig as any)?.domain || '';
    const base = domain.endsWith('/') ? domain : domain + '/';
    return `${base}files/${encodeURIComponent(userId ?? '')}/${encodeURIComponent(projectId ?? '')}/${encodeURIComponent(parentId ?? '')}/${encodeURIComponent(elementId ?? '')}/${encodeURIComponent(secretId ?? '')}`;
  };

  // Pas 1.3: primul autosave vine repede — 5s după prima editare nesalvată,
  // apoi intervalul constant de 30s preia. Un singur timeout pending; dacă
  // între timp s-a salvat deja, tick-ul e un no-op (saveCurrentChanges
  // întoarce imediat când isSaved). Curățat în stopAutoSaveTimer.
  const FIRST_AUTOSAVE_DELAY_MS = 5000;
  let firstSaveTimer: number | null = null;
  const scheduleQuickFirstSave = () => {
    if (firstSaveTimer !== null) return;
    firstSaveTimer = window.setTimeout(() => {
      firstSaveTimer = null;
      void autoSaveDraft();
    }, FIRST_AUTOSAVE_DELAY_MS);
  };

  const registerChangeDetection = () => {
    const unregisterChangeListener = registerChangeListener(() => {
      if (!isLoadingDraft.value) { editRevision.value++; isSaved.value = false; scheduleQuickFirstSave(); }
    });

    // Return cleanup function
    return unregisterChangeListener;
  };


  const draftTime = (value: unknown) => { const numeric = Number(value); return Number.isFinite(numeric) ? numeric : Date.parse(String(value)) || 0; };
  const readDrafts = async (sourceId: string) => {
    if (isDemoSourceId(sourceId)) return [];
    const response = await AutoService.fetchOrder(`drafts/${sourceId}`);
    if (!Array.isArray(response?.Items)) throw new Error('Invalid draft list');
    return response.Items.filter((draft: any) => draft.contentReady !== false).sort((a: any, b: any) =>
      draftTime(b.updatedAt || b.createdAt) - draftTime(a.updatedAt || a.createdAt));
  };
  const fetchDrafts = async (sourceId: string) => {
    try {
      const drafts = await readDrafts(sourceId);
      if (currentTranscriptSource.value?.sourceId === sourceId) availableDrafts.value = drafts;
      return drafts;
    } catch { notify('danger', t('Failed to fetch drafts')); return []; }
  };

  let sourceTransition = false;
  const withSourceTransition = async (load: () => Promise<boolean>): Promise<boolean> => {
    if (sourceTransition || isLoadingDraft.value) return false;
    sourceTransition = true;
    try {
      if (!await saveCurrentChanges()) return false;
      isLoading.value = true; isLoadingDraft.value = true;
      return await load();
    } catch { notify('danger', t('An error occurred while loading the transcript')); return false; }
    finally { isLoadingDraft.value = false; isLoading.value = false; sourceTransition = false; }
  };
  const decodeTranscript = (content: unknown, fileName?: string, contentType?: string): { type: string; data: string; settings?: any; fileName?: string } => {
    // Object sources (axios-parsed draft JSON) are explicitly JSON, so a JSON
    // draft envelope keeps its meaning even under a .txt original file name.
    // Raw strings use the caller's content type (signed-URL header) or none —
    // a declared .txt body then stays plain text, provider SRT/VTT parses as
    // its real format, and JSON is only sniffed when undeclared.
    const declaredType = typeof content === 'string' ? contentType : 'application/json';
    const text = typeof content === 'string' ? content : JSON.stringify(content);
    let doc;
    try {
      doc = parseTranscriptDocument(text, { fileName, contentType: declaredType });
    } catch (error: any) {
      if (error instanceof TranscriptDocumentError) throw new Error(error.message);
      throw error;
    }
    if (doc.kind === 'text') {
      // Untimed text never becomes fake subtitle cues; the text viewer owns it.
      return { type: 'text-document', data: doc.text, fileName: doc.fileName };
    }
    // Timed: JSON stays JSON for JsonSrtLoader; SRT/VTT text stays text so the
    // contract-aware TextSrtLoader parses the real format (never SRT-misparsed).
    if (/^\s*[\[{]/.test(text.trim())) {
      try {
        const parsed = JSON.parse(text);
        return { type: 'json', data: timedDocumentToJson(doc as any), settings: (parsed as any).settings, fileName };
      } catch { return { type: 'json', data: timedDocumentToJson(doc as any), fileName }; }
    }
    return { type: 'text', data: text, fileName };
  };
  const sourceMedia = (source: TranscriptSource): string => {
    if (source.audioUrl) return source.audioUrl;
    const outputId = source.element?.PK?.startsWith('OUTPUT#') ? source.element.PK.slice(7) : '';
    const found = outputId ? findInputElementForOutput(outputId) : null;
    const media = found && (found.output as any).isShort
      ? useDashboardStore().outputElements?.[outputId]?.find((item: IOElement) => ['video', 'audio'].includes(item.filetype || ''))
      : found?.inputElement;
    if (!media || media.status === 'file-deleted' || (media as any).sourceDeletedAt) return '';
    return buildFileUrl(media.userId || '', media.projectId || '', media.PK.split('#')[1], media.SK.split('#')[1], media.secretId || '');
  };
  const openTextDocument = (opts: { transcriptId?: string | null; sourceId: string; fileName?: string; language?: string; provenance?: string; originalAvailable?: boolean; initialText?: string | null; initialDraftId?: string | null; initialDrafts?: any[] }) => {
    currentTranscriptSource.value = null;
    isTextDocument.value = true;
    textTranscriptId.value = opts.transcriptId || null;
    textSourceId.value = opts.sourceId;
    textFileName.value = opts.fileName || '';
    textLanguage.value = opts.language || '';
    textProvenance.value = opts.provenance || '';
    textOriginalAvailable.value = opts.originalAvailable;
    textInitialText.value = opts.initialText ?? null;
    textInitialDraftId.value = opts.initialDraftId ?? null;
    textInitialDrafts.value = Array.isArray(opts.initialDrafts) ? [...opts.initialDrafts] : [];
    showTextEditor.value = true; showSrtEditor.value = false;
    isSRTLoaded.value = false;
    return true;
  };
  const closeTextDocument = () => {
    showTextEditor.value = false; isTextDocument.value = false;
    textTranscriptId.value = null; textSourceId.value = null;
    textInitialText.value = null; textInitialDraftId.value = null; textInitialDrafts.value = [];
    textSaver = null;
  };
  const adoptLoadedSource = async (source: TranscriptSource, draftId: string | null, drafts: any[]) => {
    if ((source as any).type === 'text-document') {
      return openTextDocument({
        transcriptId: (source as any).transcriptId || null,
        sourceId: source.sourceId,
        fileName: (source as any).fileName || source.element?.fileName || '',
        language: source.language,
        provenance: (source as any).provenance,
        originalAvailable: (source as any).originalAvailable,
        initialText: (source as any).data ?? null,
        initialDraftId: draftId,
        initialDrafts: drafts,
      });
    }
    if (!await loadSrtData(source)) return false;
    isDemoMode.value = isDemoSourceId(source.sourceId);
    closeTextDocument();
    currentTranscriptSource.value = source;
    currentDraftId.value = draftId;
    availableDrafts.value = drafts;
    videoUrl.value = sourceMedia(source);
    isVideoLoaded.value = !!videoUrl.value;
    isS3Video.value = !!videoUrl.value && !videoUrl.value.startsWith('blob:');
    isYouTubeVideo.value = false; isVimeoVideo.value = false; isDailymotionVideo.value = false;
    isAudioOnly.value = false;
    if (videoUrl.value) checkFileType();
    setSrtLanguage(source.language || 'ro');
    fileName.value = source.element?.fileName || source.fileName || '';
    isSRTLoaded.value = true; canRevert.value = !!(source.element || source.transcriptId);
    editRevision.value++; isSaved.value = true; showSrtEditor.value = true;
    if (!isDemoMode.value) startAutoSaveTimer();
    return true;
  };
  const readOriginalElement = async (element: IOElement, sourceId: string, audioUrl?: string): Promise<TranscriptSource> => {
    const url = buildFileUrl(element.userId || '', element.projectId || '', element.PK.split('#')[1], element.SK.split('#')[1], element.secretId || '');
    const response = await fetch(url);
    if (!response.ok) throw new Error('Original content unavailable');
    let headerType: string | undefined;
    try { headerType = response.headers?.get?.('content-type') || undefined; } catch { headerType = undefined; }
    return { sourceId, ...decodeTranscript(await response.text(), (element as any).fileName, headerType), language: element.language, element, audioUrl: audioUrl || '' };
  };

  // Create a new function to select a specific draft
  const selectDraft = (sourceId: string, versionId: string) => withSourceTransition(async () => {
    if (currentTranscriptSource.value?.sourceId !== sourceId) return false;
    const candidate = { ...currentTranscriptSource.value, ...decodeTranscript(await getDraftContent(sourceId, versionId), (currentTranscriptSource.value as any)?.fileName) };
    return adoptLoadedSource(candidate, versionId, availableDrafts.value);
  });

  const resetSrtEditor = async () => {
    if (currentTranscriptSource.value?.element || currentTranscriptSource.value?.transcriptId) {
      const confirmed = await confirmationDialog.value?.showConfirmation(
        t('Revert SRT'),
        t('Do you want to revert to original information?')
      );
      if (confirmed) await loadOriginalContent();
    }
  };

  const resetEditor = () => {
    if (baseElement.value) {
      void editElement(baseElement.value, true, deadlineTime.value, videoUrl.value);
    }
  }

  const editElement = (element: IOElement, use_original: boolean, deadline: string, audioUrl?: string) => withSourceTransition(async () => {
    if (deadline && (!Number.isFinite(Date.parse(deadline)) || Date.parse(deadline) - Date.now() <= 30000)) {
      notify('warning', t('Cannot open editor - deadline is too close')); return false;
    }
    const sourceId = element.elementId || element.SK.split('#').pop()!;
    const drafts = await readDrafts(sourceId);
    const draftId = !use_original && drafts.length ? drafts[0].versionId : null;
    const source: TranscriptSource = draftId
      ? { sourceId, ...decodeTranscript(await getDraftContent(sourceId, draftId), (element as any).fileName), language: element.language, element, audioUrl: audioUrl || '' }
      : await readOriginalElement(element, sourceId, audioUrl);
    if (!await adoptLoadedSource(source, draftId, drafts)) return false;
    deadlineTime.value = deadline; baseElement.value = element; startDeadlineTimer(deadline);
    return true;
  });

  // Find the input element backing an OUTPUT element by scanning the
  // dashboardStore's already-loaded input/output maps (the same lookup
  // loadSource has always used). Returns the parent inputId, its first
  // input element and the OUTPUT record itself, or null when the maps don't
  // contain the link. Shared with the in-editor TranscribePanel (F6), which
  // needs inputId/inputElementId.
  const findInputElementForOutput = (outputId: string): { inputId: string; inputElement: IOElement; output: IOItem } | null => {
    const dashboardStore = useDashboardStore();
    // Get references to dashboard store state
    const { inputElements, outputs } = storeToRefs(dashboardStore);
    // Search through all inputs to find the one containing this output
    for (const [inputId, inputElementsList] of Object.entries(inputElements.value)) {
      if (!inputElementsList || inputElementsList.length === 0) continue;
      // Check if this input has any outputs matching our target output
      const matchingOutput = outputs.value[inputId]?.find(
        output => output.SK.split('#')[1] === outputId
      );
      if (matchingOutput) {
        return { inputId, inputElement: inputElementsList[0], output: matchingOutput };
      }
    }
    return null;
  };

  const loadSource = () => withSourceTransition(async () => {
    const current = currentTranscriptSource.value;
    if (!current) return false;
    const source = currentDraftId.value
      ? { ...current, ...decodeTranscript(await getDraftContent(current.sourceId, currentDraftId.value), (current as any).fileName) }
      : current;
    return adoptLoadedSource(source, currentDraftId.value, availableDrafts.value);
  });

  const canRevert = ref(false);

  const deleteDraft = async (sourceId: string, versionId: string) => {
    try {
      if (!sourceId || !versionId) {
        console.error('Invalid sourceId or versionId for deleting draft');
        return false;
      }
      // check draft exists in availableDrafts
      const draftExists = availableDrafts.value.some(draft => draft.versionId === versionId);
      if (!draftExists) {
        console.error('Draft does not exist:', versionId);
        notify('danger', t('Draft does not exist'));
        return false;
      }
      // Remove draft from availableDrafts
      const response = await AutoService.delete(`drafts/${sourceId}/versions/${versionId}`, null);
      availableDrafts.value = availableDrafts.value.filter(draft => draft.versionId !== versionId);
      return true;
    } catch (error) {
      console.error('Error deleting draft:', error);
      notify('danger', t('Failed to delete draft'));
      return false;
    }
  };

  const handleLoadSrt = (source: TranscriptSource) => withSourceTransition(async () => {
    const candidate = { ...source, sourceId: source.sourceId || crypto.randomUUID() };
    // Local imports carry the file name in sourceId when dropped via SrtDropZone;
    // use it as the format hint so VTT/TXT are not mis-parsed as SRT.
    const hint = (candidate as any).fileName || candidate.sourceId;
    let decoded: any;
    try {
      const raw = (candidate as any).data;
      const text = typeof raw === 'string' ? raw : JSON.stringify(raw);
      decoded = decodeTranscript(text, hint);
    } catch (error: any) {
      notify('danger', error?.message || 'Unsupported transcript content');
      return false;
    }
    const normalized = { ...candidate, ...decoded } as any;
    if (decoded.type === 'text-document') {
      if (!await adoptLoadedSource(normalized, null, [])) return false;
      // Untimed local imports route to the text viewer; saving happens there
      // with a valid text document payload (no fake cue timings).
      return true;
    }
    if (!await adoptLoadedSource(normalized, null, [])) return false;
    // A local import has not yet been persisted as a draft.
    editRevision.value++; isSaved.value = false;
    return true;
  });

  // Pas 1.2 — deschide assetul demo public în editor, fără cont. Sursa nu
  // are element/transcriptId: nimic nu se citește sau se scrie server-side,
  // iar adoptLoadedSource setează isDemoMode (fără autosave/drafturi).
  const openDemoTranscript = (srtText: string, audioUrl?: string) => withSourceTransition(async () => {
    const source = buildDemoSource(srtText, audioUrl) as unknown as TranscriptSource;
    if (!await adoptLoadedSource(source, null, [])) return false;
    isSaved.value = true;
    return true;
  });

  const editTranscript = (transcript: Transcript) => withSourceTransition(async () => {
    const sourceId = transcript.SK.split('#').pop()!;
    const drafts = await readDrafts(sourceId);
    const draftId = drafts[0]?.versionId || null;
    let content: any;
    if (draftId) content = await getDraftContent(sourceId, draftId);
    else if (transcript.transcriptText?.output?.segments) content = transcript.transcriptText;
    else {
      const domain = (useSessionStore().appConfig as any)?.domain || '';
      const response = await fetch(`${domain.replace(/\/$/, '')}/files/${encodeURIComponent(transcript.PK.split('#')[1])}/prediction_${encodeURIComponent(transcript.predictionId || '')}.json`);
      if (!response.ok) throw new Error('Original transcript unavailable');
      // Prediction bytes are raw provider output; the signed-URL content type
      // travels with them so a .txt-named transcript never masks JSON.
      let headerType: string | undefined;
      try { headerType = response.headers?.get?.('content-type') || undefined; } catch { headerType = undefined; }
      const rawText = await response.text();
      return adoptLoadedSource({ sourceId, ...decodeTranscript(rawText, (transcript as any).fileName, headerType), language: transcript.language }, draftId, drafts);
    }
    return adoptLoadedSource({ sourceId, ...decodeTranscript(content, (transcript as any).fileName), language: transcript.language }, draftId, drafts);
  });

  // Add new ref for deadline timer
  const deadlineTimer = ref<number | null>(null);

  // Function to start deadline timer
  const startDeadlineTimer = (deadline: string) => {
    if (deadlineTimer.value) {
      clearTimeout(deadlineTimer.value);
    }

    if (!deadline || deadline.trim() === '') {
      return;
    }
 
    const deadlineTime = new Date(deadline).getTime();
    const closeTime = deadlineTime - 30000; // 30 seconds before deadline
    const now = Date.now();
    const timeUntilClose = closeTime - now;

    if (timeUntilClose <= 0) {
      notify('warning', t('Deadline has passed or is too close'));
      closeSrtEditor();
      return;
    }

    deadlineTimer.value = window.setTimeout(async () => {
      notify('warning', t('Closing editor due to approaching deadline'));
      await unloadSrtData(false); // Auto-save before closing
      closeSrtEditor();
    }, timeUntilClose);
  };

  // Function to stop deadline timer
  const stopDeadlineTimer = () => {
    if (deadlineTimer.value) {
      clearTimeout(deadlineTimer.value);
      deadlineTimer.value = null;
    }
  };

  // Add new ref for auto-save timer
  const autoSaveTimer = ref<number | null>(null);

 
  
  const loadSrtData = async (source: TranscriptSource) => {

    if ((source as any).type === 'text-document') {
      // Untimed text never loads as subtitle cues; the text viewer owns it.
      console.error('Untimed text routed to text viewer, not subtitle cues');
      return false;
    }
    let loader: SrtLoader;
    switch (source.type) {
      case 'api':
        loader = new ApiSrtLoader();
        break;
      case 'file':
        loader = new FileSrtLoader();
        break;
      case 'text':
        loader = new TextSrtLoader();
        break;
      case 'json':
        loader = new JsonSrtLoader();
        break;
      default:
        throw new Error(`Unsupported SRT source type: ${source.type}`);
    }


    try {
      const fileHint = (source as any).fileName || source.sourceId;
      let data: any;
      try {
        data = source.type === 'text'
          ? await (loader as TextSrtLoader).load(source.data, fileHint)
          : await loader.load(source.data);
      } catch (error: any) {
        if (isUntimedTextError(error)) {
          // VTT/TXT detection already routed untimed text to text-document;
          // reaching here means a timed loader saw plain text: surface it.
          notify('danger', t('Untimed text transcript: open with the text viewer'));
        }
        throw error;
      }
      if (!Array.isArray(data)) throw new Error('Invalid subtitle content');
      // NOTĂ (Pas 1.3): câmpurile autoSave/autoSaveInterval sunt metadata
      // moștenită în forma settings cerută de subtitleStore.replaceTranscript
      // și persistată în drafturi — NIMENI nu le citește pentru comportament.
      // Autosave-ul real (startAutoSaveTimer, 30s + primul tick la 5s) pornește
      // mereu. Valoarea e `true` ca metadata să nu mai mintă că autosave e oprit;
      // eliminarea completă a câmpurilor cere schimbarea contractului store +
      // a formei persistate în drafturi, în afara acestui pas sigur.
      const defaultjsonSettings = {
        maxCPL: 42,
        maxCPS: 15,
        maxLines: 3,
        autoSave: true,
        autoSaveInterval: 240
      };
      let jsonSettings = source.settings || null;
      if (source.element && source.element.settings) {
        try {
          jsonSettings = JSON.parse(source.element.settings.replace(/\\"/g, '"'));
        } catch (error) {
          console.error('Error parsing settings:', source);
        }
      }

      const adoptionSettings = {
        maxCPS: jsonSettings?.maxCPS ?? defaultjsonSettings.maxCPS,
        maxCPL: jsonSettings?.maxCPL ?? defaultjsonSettings.maxCPL,
        maxLines: jsonSettings?.maxLines ?? defaultjsonSettings.maxLines,
        // Forțat true (vezi nota de mai sus): metadata veche `false` din
        // drafturi nu mai e propagată — autosave rulează întotdeauna.
        autoSave: true,
        autoSaveInterval: jsonSettings?.autoSaveInterval ?? defaultjsonSettings.autoSaveInterval,
      };
      try {
        await subtitleStore.replaceTranscript(source.sourceId, data, adoptionSettings);
      } catch (error: any) {
        if (!isAdoptionError(error)) throw error;
        // The store never drops edits silently: `conflict` keeps the current
        // document, `unavailable` keeps it plus the unsaved queue. Surface
        // both instead of the old silent `return false`.
        if (error.code === 'unavailable') {
          notify('danger', adoptionText('unavailable'));
          return false;
        }
        if (error.code !== 'conflict') throw error;
        // Conflict: settle debounced keystrokes into the current document
        // first so nothing is lost, then ask before replacing it — the same
        // confirmationDialog pattern as the revert flow.
        try {
          await flushPendingSubtitleUpdates(subtitleStore);
        } catch {
          notify('danger', t('Changes could not be saved. Please retry.'));
          return false;
        }
        // No mounted dialog can ask (e.g. opening from the Saved list before
        // the editor mounts): keep the document and say so — never fail
        // silent. An explicit decline below stays quiet; the user decided.
        const dialog = confirmationDialog.value;
        if (!dialog || typeof dialog.showConfirmation !== 'function') {
          notify('danger', adoptionText('conflictKept'));
          return false;
        }
        let confirmed = false;
        try {
          confirmed = await dialog.showConfirmation(
            adoptionText('conflictTitle'),
            adoptionText('conflictBody'),
          ) === true;
        } catch {
          return false;
        }
        // Declined: keep the current document and its edits exactly as is.
        if (!confirmed) return false;
        // Save the settled edits under the current draft first, then retry
        // the adoption exactly once. A second conflict means edits are still
        // arriving: report and keep the document rather than looping.
        if (!await saveCurrentChanges()) return false;
        try {
          await subtitleStore.replaceTranscript(source.sourceId, data, adoptionSettings);
        } catch (retryError: any) {
          console.error('Error reloading SRT data after conflict:', retryError);
          notify('danger', adoptionText('retryFailed'));
          return false;
        }
      }

      // Start auto-save timer after successful load (niciodată pentru demo)
      if (!isDemoSourceId(source.sourceId)) startAutoSaveTimer();

      return true;
    } catch (error) {
      console.error('Error loading SRT data:', error);
    }
    return false;
  };

  // Helper functions for language handling
  const getLanguageName = (code: any) => ISO6391.getName(code);
  const getLanguageNativeName = (code: any) => ISO6391.getNativeName(code);
  const validateLanguageCode = (code: any) => ISO6391.validate(code);

  const isVideoLoaded = ref(false);
  const isDailymotionVideo = ref(false);
  const isYouTubeVideo = ref(false);
  const isVimeoVideo = ref(false);
  const isS3Video = ref(false);
  const isIframeEmbed = ref(false);
  const isAudioOnly = ref(false);
  const fileType = ref('video/mp4');
  
  const checkFileType = () => {
    videoUrl.value = videoUrl.value.trim();
    if (videoUrl.value.length == 0) {
      return;
    }
    // get the correct file extension
    isAudioOnly.value = false;
   
    let ext = videoUrl.value.split('?')[0].split('#')[0].split('/').pop()?.toLowerCase();
    const domain = (useSessionStore().appConfig as any)?.domain || '';
    const base = domain.endsWith('/') ? domain : domain + '/';
    if (videoUrl.value.includes('youtube.com') || videoUrl.value.includes('youtu.be')) {
      isYouTubeVideo.value = true;
    } else if (videoUrl.value.includes('vimeo.com')) {
      isVimeoVideo.value = true;
    } else if (videoUrl.value.includes('dailymotion.com') || videoUrl.value.includes('dai.ly')) {
      isDailymotionVideo.value = true;
    } else if (
      videoUrl.value.includes('amazonaws.com') ||
      videoUrl.value.startsWith('s3://') ||
      // Same-origin platform media (served from CloudFront files/* in dev/prod —
      // the domain carries no 'amazonaws.com'). Without this, isS3Video stays
      // false for every dashboard-opened element and the F5 export / F6
      // transcribe gates (which key off isS3Video) never show.
      (!!domain && videoUrl.value.startsWith(base + 'files/'))
    ) {
      isS3Video.value = true;
      videoUrl.value = videoUrl.value.replace(/^s3:\/\//, 'https://');
    } else if (videoUrl.value.startsWith('<iframe') || videoUrl.value.includes('embed')) {
      isIframeEmbed.value = true;
    } else {
      ext = fileName.value.split('.')[1]?.toLowerCase();
    }
    
    switch (ext) {
      case 'mp3': isAudioOnly.value = true; fileType.value = 'audio/mp3'; break;
      case 'wav': isAudioOnly.value = true; fileType.value = 'audio/wav'; break;
      case 'aac': isAudioOnly.value = true; fileType.value = 'audio/aac'; break;
      case 'm4a': isAudioOnly.value = true; fileType.value = 'audio/m4a'; break;
      case 'flac': isAudioOnly.value = true; fileType.value = 'audio/flac'; break;
      case 'opus': isAudioOnly.value = true; fileType.value = 'audio/opus'; break;
      case 'mp4': fileType.value = 'video/mp4'; break;
      case 'webm': fileType.value = 'video/webm'; break;
      case 'ogg': fileType.value = 'video/ogg'; break;
      default: fileType.value = 'video/mp4';
    }

  }


  // All save entry points use the same serialized, revision-aware draft path.
  const unloadSrtData = async (_verbose: boolean): Promise<boolean> => saveCurrentChanges();

  const originalCloseSrtEditor = async () => {
    // Stop auto-save timer
    stopAutoSaveTimer();
    stopDeadlineTimer();
 
    // Reset draft-related state
    currentDraftId.value = null;
    availableDrafts.value = [];
 
    // Reset UI state
    showSrtEditor.value = false;
    isVideoLoaded.value = false;
    isDailymotionVideo.value = false;
    isYouTubeVideo.value = false;
    isVimeoVideo.value = false;
    isS3Video.value = false;
    isIframeEmbed.value = false;
    fileType.value = 'video/mp4';
    isSRTLoaded.value = false;
    isAudioLoaded.value = false;
    videoUrl.value = '';
  };
 
  const srtLanguage = ref('');
  const languageCPS = ref(15);
  const fileName = ref('');
  const videoUrl = ref('');
  const playbackSpeed = ref('1.0');
  const currentTime = ref(0);
  const isPlaying = ref(false);
  const showSubtitles = ref(true);
  const videoDuration = ref(0);
 
  const isLoaded = ref(false);
  const isAudioLoaded = ref(false);
  const isSRTLoaded = ref(false);
  const isLoading = ref(false);
 
  const setSrtLanguage = (language: string) => {
    srtLanguage.value = language;
    // if asian language, set default CPS to 6 otherwise 15
    if (language === 'ja' || language === 'ko' || language === 'zh' || language === 'th' || language === 'vi' || language === 'cn') {
      languageCPS.value = 6;
    } else {
      languageCPS.value = 15;
    }
  }
  
  const handleLoadVideo = (file: File) => {
      videoUrl.value = URL.createObjectURL(file);
      fileName.value = file.name;
      isVideoLoaded.value = true;
  };
 
  const originalOpenSrtEditor = async () => loadSource();

  const isAutoSaving = ref(false);
  const isSaved = ref(true);
  // Pas 1.0a: eșecul de salvare în cont (creare draft / upload) e stare
  // persistentă vizibilă în indicatorul editorului până la primul succes —
  // nu doar un toast unic după care tăcere.
  const draftSaveFailed = ref(false);

  // Public dirty-flag writer: children receive the editor bag as a prop, so
  // they must not assign into props.editor.* directly — they call this.
  const markDirty = () => {
    if (!isLoadingDraft.value) { editRevision.value++; isSaved.value = false; scheduleQuickFirstSave(); }
  };

  // Adopt a freshly loaded transcript as the current source (used after auto
  // transcription finishes). Same ownership rule as markDirty.
  const adoptTranscriptSource = (payload: { sourceId: string; element: any; type: string; data: unknown }) => withSourceTransition(async () => {
    const previous = currentTranscriptSource.value;
    const source: TranscriptSource = { ...previous, ...payload } as TranscriptSource;
    return adoptLoadedSource(source, null, await readDrafts(payload.sourceId));
  });
  // Draft switches are asynchronous: while a selected version is being
  // fetched/loaded, the old editor contents must not be autosaved under the
  // newly selected draft ID.
  const isLoadingDraft = ref(false);
  const lastSaveTime = ref<Date | null>(null);
  const autoSaveInterval = ref(AUTOSAVE_BASE_INTERVAL); // 30 seconds by default
  const showDraftStatus = ref(true);
  // Add this method to create a new draft version (checkpoint)
  const createDraftVersion = async (sourceId: string, metadata: { name: string, description: string }) => {
    if (isDemoSourceId(sourceId)) return { success: false, error: 'Demo mode: nothing is saved to an account' };
    if (checkpointBusy || sourceTransition || isLoadingDraft.value || currentTranscriptSource.value?.sourceId !== sourceId) return { success: false, error: 'Editor is busy or source changed' };
    checkpointBusy = true;
    try {
      const checkpointFileName = (fileName.value || currentTranscriptSource.value?.fileName || '').slice(0, 500) || undefined;
      const checkpointLanguage = (srtLanguage.value || currentTranscriptSource.value?.language || '').slice(0, 16) || undefined;
      const newDraft = await AutoService.fetchOrder(
        `drafts/${sourceId}/versions`,
        null,
        {
          name: metadata.name,
          description: metadata.description,
          ...(checkpointFileName ? { fileName: checkpointFileName } : {}),
          ...(checkpointLanguage && /^[A-Za-z-]{2,16}$/.test(checkpointLanguage) ? { language: checkpointLanguage } : {}),
        }
      );
      
      if (newDraft && newDraft.Attributes && newDraft.Attributes.versionId) {
        // add to availableDrafts
        const revision = editRevision.value;
        if (!await saveDraftContent(sourceId, newDraft.Attributes.versionId)) return { success: false, error: 'Save failed' };
        if (currentTranscriptSource.value?.sourceId !== sourceId) return { success: false, error: 'Source changed' };
        availableDrafts.value.unshift({ ...newDraft.Attributes, contentReady: true });
      currentDraftId.value = newDraft.Attributes.versionId;
        isSaved.value = editRevision.value === revision;
        
        return {
          success: true,
          versionId: newDraft.Attributes.versionId
        };
      }
      return { success: false, error: 'Failed to create draft' };
    } catch (error) {
      console.error('Error creating draft version:', error);
      return { success: false, error };
    } finally { checkpointBusy = false; }
  };

  // Add method to save draft content
  const saveDraftContent = async (sourceId: string, versionId: string): Promise<boolean> => {
    try { await flushPendingSubtitleUpdates(subtitleStore); }
    catch { notify('danger', t('Changes could not be saved. Please retry.')); return false; }
    if (!sourceId || !versionId || currentTranscriptSource.value?.sourceId !== sourceId || !subtitleStore.srtData) return false;
    const revision = editRevision.value;
    const snapshot = JSON.parse(JSON.stringify(subtitleStore.srtData));
    const settings = { maxCPS: subtitleStore.maxCPS, maxCPL: subtitleStore.maxCPL, maxLines: subtitleStore.maxLines,
      autoSave: subtitleStore.autoSave, autoSaveInterval: subtitleStore.autoSaveInterval };
    const save = async () => {
      isAutoSaving.value = true;
      try {
        const data = JSON.stringify({ ...JSON.parse(await new JsonSrtLoader().unload(snapshot)), settings });
        await AutoService.uploadToS3(`drafts/${sourceId}/versions/${versionId}/content`, data);
        if (currentTranscriptSource.value?.sourceId === sourceId && currentDraftId.value === versionId && editRevision.value === revision) isSaved.value = true;
        lastSaveTime.value = new Date();
        draftSaveFailed.value = false;
        return true;
      } catch {
        draftSaveFailed.value = true;
        notify('danger', t('Changes could not be saved. Please retry.'));
        return false;
      } finally { isAutoSaving.value = false; }
    };
    saveChain = saveChain.then(save, save);
    return saveChain;
  };

  const saveCurrentChanges = async (): Promise<boolean> => {
    // Demo fără cont: editările sunt locale prin definiție — „salvarea"
    // reușește trivial, fără niciun apel de cont/draft.
    if (isDemoMode.value) return true;
    if (checkpointBusy) return false;
    // Text editor save-before-switch: never silently drop text. When the text
    // view is shown the controller must be mounted and ready; a missing, not
    // ready, or busy controller blocks the transition so edits cannot arrive
    // after the initial save before the new source is adopted.
    if (showTextEditor.value) {
      if (!textSaver) return false;
      if (textSaver.busy?.() === true) return false;
      if (textSaver.ready && !textSaver.ready()) return false;
      if (textSaver.isDirty()) {
        if (!await textSaver.save()) return false;
      }
    }
    try { await flushPendingSubtitleUpdates(subtitleStore); }
    catch { notify('danger', t('Changes could not be saved. Please retry.')); return false; }
    await saveChain;
    if (isSaved.value || !currentTranscriptSource.value?.sourceId) return true;
    if (isLoadingDraft.value) return false;
    const sourceId = currentTranscriptSource.value.sourceId;
    if (!currentDraftId.value) {
      if (!creatingDraft) creatingDraft = createInitialDraft(sourceId).finally(() => { creatingDraft = undefined; });
      if (!await creatingDraft) return false;
    } else if (!await saveDraftContent(sourceId, currentDraftId.value)) return false;
    return isSaved.value; // New edits made during the upload still need saving.
  };

const createInitialDraft = async (sourceId: string) => {
  try {
    if (!sourceId) {
      console.error('No sourceId provided for creating initial draft');
      return false;
    }

    const draftName = `Initial Draft ${new Date().toLocaleString()}`;
    // Original file name/language travel into draft metadata for inventory.
    const draftFileName = (fileName.value || currentTranscriptSource.value?.fileName || '').slice(0, 500) || undefined;
    const draftLanguage = (srtLanguage.value || currentTranscriptSource.value?.language || '').slice(0, 16) || undefined;
    const newDraft = await AutoService.fetchOrder(
      `drafts/${sourceId}/versions`,
      null,
      {
        name: draftName,
        description: 'Initial editing session',
        ...(draftFileName ? { fileName: draftFileName } : {}),
        ...(draftLanguage && /^[A-Za-z-]{2,16}$/.test(draftLanguage) ? { language: draftLanguage } : {}),
      }
    );

    
    if (newDraft && newDraft.Attributes && newDraft.Attributes.versionId) {
      const revision = editRevision.value;
      if (!await saveDraftContent(sourceId, newDraft.Attributes.versionId)) return false;
      if (currentTranscriptSource.value?.sourceId !== sourceId) return false;
      availableDrafts.value.unshift({ ...newDraft.Attributes, contentReady: true });
      currentDraftId.value = newDraft.Attributes.versionId;
      isSaved.value = editRevision.value === revision;
      lastSaveTime.value = new Date();
      draftSaveFailed.value = false;
      // Draft creation recovered — allow a one-time warning again on a future
      // failure episode
      draftWarningShown = false;
      return true;
    }
  } catch (error) {
    console.error('Error creating initial draft:', error);
    if (!draftWarningShown) {
      draftWarningShown = true;
      notify('warning', t('Could not create initial draft, changes may not be saved automatically'));
    } else {
      // Warned once already — keep the history, stop the toast flood
      useLogStore().addMessage({ message: 'Could not create initial draft, changes may not be saved automatically', type: 'warning' });
    }
  }
  // No draft could be created (or the response carried no versionId): edits
  // are NOT reaching the account. Persistent indicator until a save succeeds.
  draftSaveFailed.value = true;
  return false;
};


  // Add the missing getDraftContent method
  const getDraftContent = async (sourceId: string, versionId: string) => {
    try {
      const response = await AutoService.getfromS3(`drafts/${sourceId}/versions/${versionId}/content`);
      return response;
    } catch (error) {
      console.error('Error fetching draft content:', error);
      throw error;
    }
  };

  // Modified autoSaveDraft to create a new draft if needed
  const autoSaveDraft = async () => {
    if (isLoadingDraft.value || isAutoSaving.value) return false;
    return saveCurrentChanges();
  };

  // Start auto-save timer
  const startAutoSaveTimer = () => {
    if (autoSaveTimer.value) {
      clearInterval(autoSaveTimer.value);
    }
    
    autoSaveTimer.value = window.setInterval(async () => {
      await autoSaveDraft();
    }, autoSaveInterval.value);
  };

  // Stop auto-save timer
  const stopAutoSaveTimer = () => {
    if (autoSaveTimer.value) {
      clearInterval(autoSaveTimer.value);
      autoSaveTimer.value = null;
    }
    if (firstSaveTimer !== null) {
      clearTimeout(firstSaveTimer);
      firstSaveTimer = null;
    }
  };


  const loadOriginalContent = () => withSourceTransition(async () => {
    const current = currentTranscriptSource.value;
    if (!current) return false;
    let source: TranscriptSource;
    if (current.transcriptId) {
      const response = await AutoService.fetchOrder('transcripts', current.transcriptId);
      // Only the actual original artifact may back an "original" load; never
      // silently alias the latest draft contentUrl with the original label.
      const url = response.originalUrl;
      if (!url) throw new Error('Original content unavailable');
      const content = await fetch(url, { credentials: 'omit' });
      if (!content.ok) throw new Error('Original content unavailable');
      let headerType: string | undefined;
      try { headerType = content.headers?.get?.('content-type') || undefined; } catch { headerType = undefined; }
      source = { ...current, ...decodeTranscript(await content.text(), (current as any).fileName, headerType), audioUrl: response.sourceUrl || '' };
    } else if (current.element) source = await readOriginalElement(current.element, current.sourceId, current.audioUrl);
    else return false;
    return adoptLoadedSource(source, null, availableDrafts.value);
  });

  // Override the existing openSrtEditor method to start auto-save
  const openSrtEditor = async () => {
    return originalOpenSrtEditor();
  };

  // Override the closeSrtEditor method to stop auto-save
  const closeSrtEditor = async () => {
    if (sourceTransition || isLoadingDraft.value) return false;
    if (!await saveCurrentChanges()) return false;
    stopAutoSaveTimer();
    await originalCloseSrtEditor();
    isDemoMode.value = false;
    currentTranscriptSource.value = null;
    closeTextDocument();
    return true;
  };

  const isKnownMissingDraftError = (error: any): boolean => {
    const status = error?.status ?? error?.response?.status ?? error?.$metadata?.httpStatusCode;
    if (status === 404) return true;
    const code = String(error?.code || error?.name || '');
    const message = String(error?.message || '');
    return /NoSuchKey|NotFound|404|CONTENT_UNAVAILABLE/.test(`${code} ${message}`) && !/AccessDenied|403|Forbidden|transient|timeout|network/i.test(`${code} ${message}`);
  };

  const openRetainedTranscript = (id: string) => withSourceTransition(async () => {
    const response = await AutoService.fetchOrder('transcripts', id);
    const row = response.transcript;
    const sourceId = row.sourceId || id;
    const drafts = await readDrafts(sourceId);
    // Backend HEAD-probes actual draft survivors: response.draft.versionId is
    // the authoritative surviving selection. Never re-choose first metadata.
    const actualDraftId: string | null = response.draft?.versionId || null;
    let adoptedDraftId: string | null = actualDraftId;
    let content: unknown;
    // Signed-URL fallback bytes carry their real content type so a JSON draft
    // envelope keeps its meaning under a .txt original file name, while a
    // declared .txt body stays plain text. Draft-API objects are JSON by
    // construction (see decodeTranscript).
    let fallbackType: string | undefined;
    const fetchFallback = async (fallback: string): Promise<string> => {
      const download = await fetch(fallback, { credentials: 'omit' });
      if (!download.ok) throw new Error('Content unavailable');
      try { fallbackType = download.headers?.get?.('content-type') || undefined; } catch { fallbackType = undefined; }
      return download.text();
    };
    if (actualDraftId) {
      try {
        content = await getDraftContent(sourceId, actualDraftId);
      } catch (error: any) {
        // Known-missing only: fall back to a verified original/recovered URL
        // and adopt it explicitly as original (draftId null). Transient/403
        // failures are never masked as missing; the open fails and the
        // current view is preserved (withSourceTransition returns false).
        if (!isKnownMissingDraftError(error)) throw error;
        const fallback = response.originalUrl || response.recoveredUrl || response.contentUrl;
        if (!fallback) {
          if (response.code === 'CONTENT_AVAILABILITY_UNKNOWN') {
            notify('danger', adoptionText('availabilityUnknown'));
          }
          throw new Error(response.code === 'CONTENT_UNAVAILABLE' ? 'Content unavailable' : 'Content availability unknown');
        }
        content = await fetchFallback(fallback);
        adoptedDraftId = null;
      }
    } else {
      const fallback = response.contentUrl || response.draftUrl || response.originalUrl || response.recoveredUrl;
      if (!fallback) {
        // Identity without usable content: the backend reports 200 + `code`
        // instead of URLs. CONTENT_AVAILABILITY_UNKNOWN is explicitly
        // retryable (transient probe failure); CONTENT_UNAVAILABLE means the
        // bytes are confirmed gone while the catalog row survives.
        if (response.code === 'CONTENT_AVAILABILITY_UNKNOWN') {
          notify('danger', adoptionText('availabilityUnknown'));
        }
        throw new Error(response.code === 'CONTENT_UNAVAILABLE' ? 'Content unavailable' : 'Content availability unknown');
      }
      content = await fetchFallback(fallback);
      adoptedDraftId = null;
    }
    let decoded: any;
    try {
      decoded = decodeTranscript(content, row.fileName, fallbackType);
    } catch (error: any) {
      notify('danger', error?.message || 'Unsupported transcript content');
      return false;
    }
    const source: TranscriptSource = { sourceId, transcriptId: id, ...decoded,
      language: row.language, audioUrl: response.sourceUrl || '', projectId: row.projectId, fileName: row.fileName,
      provenance: row.provenance, originalAvailable: row.originalAvailable } as any;
    return adoptLoadedSource(source, adoptedDraftId, drafts);
  });


  const closeTextEditor = async (): Promise<boolean> => {
    if (showTextEditor.value) {
      if (!textSaver) return false;
      if (textSaver.busy?.() === true) return false;
      if (textSaver.ready && !textSaver.ready()) return false;
      const canClose = textSaver.canClose ? await textSaver.canClose() : (textSaver.isDirty() ? await textSaver.save() : true);
      if (!canClose) return false;
    }
    closeTextDocument();
    return true;
  };

  return {
    currentTranscriptSource,
    openRetainedTranscript,
    saveCurrentChanges,
    showSrtEditor,
    showTextEditor,
    isTextDocument,
    textTranscriptId,
    textSourceId,
    textFileName,
    textLanguage,
    textProvenance,
    textOriginalAvailable,
    textInitialText,
    textInitialDraftId,
    textInitialDrafts,
    registerTextSaver,
    clearTextSaver,
    isTextSaverReady,
    isTextDirty,
    isTextBusy,
    openTextDocument,
    closeTextDocument,
    closeTextEditor,
    canRevert,
    resetSrtEditor,
    handleLoadVideo,
    openSrtEditor,
    srtLanguage,
    languageCPS,
    loadSrtData,
    unloadSrtData,
    isLoaded,
    isAudioLoaded,
    isLoading,
    isSRTLoaded,
    confirmationDialog,
    showDraftModal,
    fileName,
    videoDuration,
    currentTime,
    isPlaying,
    isAudioOnly,
    startAutoSaveTimer,
    stopAutoSaveTimer,
    showSubtitles,
    videoUrl,
    deleteDraft,
    playbackSpeed,
    resetEditor,
    closeSrtEditor,
    editTranscript,
    getLanguageName,
    getLanguageNativeName,
    validateLanguageCode,
    handleLoadSrt,
    openDemoTranscript,
    isDemoMode,
    editElement,
    fileType,
    isVideoLoaded,
    isDailymotionVideo,
    isYouTubeVideo,
    isVimeoVideo,
    isS3Video,
    isIframeEmbed,
    // Expose the new draft functions
    currentDraftId,
    availableDrafts,
    fetchDrafts,
    registerChangeDetection,
    selectDraft,
    isAutoSaving,
    isSaved,
    draftSaveFailed,
    markDirty,
    adoptTranscriptSource,
    lastSaveTime,
    showDraftStatus,
    createDraftVersion,
    saveDraftContent,
    getDraftContent,
    loadOriginalContent,
    findInputElementForOutput,
  };
 }
